import { respectReducedMotion } from '../utils/motion.js';

/**
 * Hero animation: an ink drop falls, hits the "paper", splashes outward,
 * and the splash resolves into the site wordmark/title reveal.
 * Uses an SVG blob (not canvas physics) for performance + crisp scaling.
 */
export function initHeroInk(gsap) {
  const hero = document.querySelector('.hero');
  if (!hero) return;

  const drop = hero.querySelector('.hero__drop');
  const splash = hero.querySelector('.hero__splash path');
  const lines = hero.querySelectorAll('.hero__title .line span');
  const eyebrow = hero.querySelector('.hero__eyebrow');
  const sub = hero.querySelector('.hero__sub');

  // Get the splash element's parent or container for color flashing
  const splashContainer = splash?.closest('.hero__splash') || splash?.parentElement;

  respectReducedMotion(
    gsap,
    (gsap) => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // 1. Splash morphs outward from a dot to an irregular ink blob
      tl.fromTo(
        splash,
        { attr: { d: 'M100,100 m-2,0 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0' }, opacity: 1 },
        {
          attr: {
            d: 'M100,60 C140,60 165,80 168,110 C171,142 145,168 108,166 C68,164 42,140 40,108 C38,78 64,60 100,60 Z',
          },
          duration: 1.6,
          ease: 'elastic.out(1, 0.6)',
        },
        '-=0.1'
      )

      // 2. CMYK flash sequence - quick color shifts on the splash
      .call(() => {
        // Cyan flash
        gsap.to(splash, {
          duration: 0.15,
          attr: { fill: '#00AEEF' },
          onComplete: () => {
            // Magenta flash
            gsap.to(splash, {
              duration: 0.20,
              attr: { fill: '#EC008C' },
              onComplete: () => {
                // Yellow flash
                gsap.to(splash, {
                  duration: 0.15,
                  attr: { fill: '#FFF200' },
                  onComplete: () => {
                    // Black flash (back to original or dark ink)
                    gsap.to(splash, {
                      duration: 0.10,
                      attr: { fill: '#1a1a1a' },
                    });
                  }
                });
              }
            });
          }
        });
      })

      // 3. Blob expands and fades out
      .to(splash, { 
        opacity: 0, 
        scale: 18, 
        transformOrigin: 'center', 
        duration: 1.1, 
        ease: 'power2.in' 
      }, '+=0.15')

      // 4. Title lines rise up out of the splash, staggered
      .fromTo(
        lines,
        { yPercent: 120, rotate: 4 },
        { yPercent: 0, rotate: 0, duration: 0.9, stagger: 0.08, ease: 'power4.out' },
        '-=0.9'
      )
      .to(eyebrow, { opacity: 1, duration: 0.5 }, '-=0.6')
      .to(sub, { opacity: 0.8, duration: 0.6 }, '-=0.4');
    },
    (gsap) => {
      gsap.set([drop, splash], { opacity: 0 });
      gsap.set(lines, { yPercent: 0 });
      gsap.set([eyebrow, sub], { opacity: 1 });
    }
  );
}